/* Collection: filter toggle + "Load more".
 *
 * Load more renders EXACTLY section.settings.products_per_page new cards per
 * click. The Liquid loop skips some products (bundle-tagged, wholesale-hidden),
 * so a single paginated page can yield fewer cards than the setting. We keep a
 * cursor over pages, pull as many pages as needed to fill a batch, and hold the
 * overflow in a buffer that is rendered first on the next click.
 */
(function () {
  'use strict';

  var GRID_ID = 'product-grid';
  var BUTTON_ID = 'load-more-button';

  var state = null;

  function grid() {
    return document.getElementById(GRID_ID);
  }

  function button() {
    return document.getElementById(BUTTON_ID);
  }

  function cards(container) {
    return Array.prototype.slice.call(container.children);
  }

  // Only cards the shopper can actually see, i.e. after any app/theme JS
  // has hidden some of them.
  function visibleCardCount() {
    var g = grid();
    if (!g) return 0;
    return cards(g).filter(function (li) {
      return !!(li.offsetParent || li.getClientRects().length);
    }).length;
  }

  function cardId(li) {
    var id = li.getAttribute && li.getAttribute('data-product-id');
    if (id) return id;
    var link = li.querySelector && li.querySelector('a[href]');
    return link ? link.getAttribute('href').split('?')[0] : null;
  }

  // Same collection, same sort + filters, just a different page, rendered
  // through the Section Rendering API so we get the grid markup only.
  function pageUrl(page) {
    var params = new URLSearchParams(window.location.search);
    params.set('page', String(page));
    if (state.sectionId) params.set('section_id', state.sectionId);
    return window.location.pathname + '?' + params.toString();
  }

  function fetchPage(page) {
    return fetch(pageUrl(page))
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fetched = doc.getElementById(GRID_ID);
        return fetched ? cards(fetched) : [];
      });
  }

  function exhausted() {
    return state.buffer.length === 0 && state.nextPage > state.totalPages;
  }

  // Resolves with exactly `count` new cards, unless the collection runs out.
  // Bound to the state it started with: if init() replaced it meanwhile (the
  // grid was re-rendered for a filter/sort/tag change) it stops and resolves empty.
  function collect(count) {
    var s = state;
    var batch = [];

    while (batch.length < count && s.buffer.length) {
      batch.push(s.buffer.shift());
    }

    function pump() {
      if (s !== state) return Promise.resolve([]);
      if (batch.length >= count || s.nextPage > s.totalPages) {
        return Promise.resolve(batch);
      }
      var page = s.nextPage++;
      return fetchPage(page).then(function (items) {
        if (s !== state) return [];
        items.forEach(function (li) {
          var id = cardId(li);
          if (id) {
            if (s.seen.has(id)) return;
            s.seen.add(id);
          }
          if (batch.length < count) batch.push(li);
          else s.buffer.push(li);
        });
        return pump();
      });
    }

    return pump();
  }

  // Cards rendered with data-pin-last="true" (theme setting pin_last_tag) must
  // stay at the end of the whole collection. Liquid only orders within one page,
  // so after every append move them back to the end, keeping their relative order.
  // Only DOM position changes: the seen set, buffer and batch counts are untouched.
  function pinLast() {
    var g = grid();
    if (!g) return;
    var pinned = cards(g).filter(function (li) {
      return li.getAttribute('data-pin-last') === 'true';
    });
    pinned.forEach(function (li) {
      g.appendChild(li);
    });
  }

  function append(items) {
    if (!items.length) return;
    var g = grid();
    if (!g) return;
    var fragment = document.createDocumentFragment();
    items.forEach(function (li) {
      fragment.appendChild(document.importNode(li, true));
    });
    g.appendChild(fragment);
    pinLast();
    document.dispatchEvent(new Event('swym:collections-loaded'));
  }

  function refreshButton() {
    var b = button();
    if (!b) return;
    b.style.display = exhausted() ? 'none' : 'inline-block';
  }

  function setBusy(busy) {
    var b = button();
    if (!b) return;
    b.disabled = busy;
    b.textContent = busy ? 'Loading...' : state.label;
  }

  // Runs collect() and appends, unless the grid was re-rendered meanwhile.
  function fill(count, label) {
    var s = state;
    s.loading = true;
    setBusy(true);
    return collect(count)
      .then(function (items) {
        if (s === state) append(items);
      })
      .catch(function (error) {
        console.error(label, error);
      })
      .then(function () {
        s.loading = false;
        if (s !== state) return;
        setBusy(false);
        refreshButton();
      });
  }

  function loadMore() {
    if (!state || state.loading || exhausted()) return Promise.resolve();
    return fill(state.perPage, 'Load more failed:');
  }

  // First page can render short for the same reason a later page can.
  function topUp() {
    if (!state || state.loading || state.toppedUp || exhausted()) return;
    state.toppedUp = true;
    var missing = state.perPage - visibleCardCount();
    if (missing <= 0) return;
    fill(missing, 'Load more top-up failed:');
  }

  function onClick(event) {
    event.preventDefault();
    loadMore();
  }

  function init() {
    var g = grid();
    // Also runs after facet/sort/tag re-renders (facets.js calls init()).
    // Drop the previous state first so in-flight requests for the old grid are discarded.
    state = null;
    pinLast();
    var b = button();
    if (!g || !b) return;

    var perPage = parseInt(g.getAttribute('data-per-page'), 10);
    if (!perPage || perPage < 1) return;

    var currentPage = parseInt(g.getAttribute('data-current-page'), 10) || 1;
    var totalPages = parseInt(g.getAttribute('data-total-pages'), 10) || 1;

    state = {
      sectionId: g.getAttribute('data-id'),
      perPage: perPage,
      nextPage: currentPage + 1,
      totalPages: totalPages,
      buffer: [],
      seen: new Set(),
      loading: false,
      toppedUp: false,
      label: (b.textContent || 'Load more').trim()
    };

    cards(g).forEach(function (li) {
      var id = cardId(li);
      if (id) state.seen.add(id);
    });

    if (b.dataset.loadMoreBound !== 'true') {
      b.addEventListener('click', onClick);
      b.dataset.loadMoreBound = 'true';
    }

    refreshButton();

    // Let app/theme scripts hide whatever they hide before we count.
    setTimeout(topUp, 300);
  }

  window.CollectionLoadMore = { init: init, loadMore: loadMore };

  function onReady() {
    var toggle = document.querySelector('.filter-toggle');
    if (toggle) {
      toggle.addEventListener('click', function () {
        var facets = document.querySelector('.facets-vertical');
        if (facets) facets.classList.toggle('filter-expanded');
      });
    }
    init();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onReady);
  } else {
    onReady();
  }
})();
